/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useLayoutEffect } from "react";
import { useHistory } from "react-router-dom";
import styles from "./AccessSubPages.module.css";
import { handleLoginClick } from "../StaticPages";

/* ============================================================================
   AccessSubPageShell.jsx

   Shared chrome for the three Medicare ACCESS Google Ads subpages. Renders
   the identity bar, the sticky nav row, the CTA band and the footer, with
   each page's own content passed in as children.

   Carried from lbpNewLandingPage.jsx without change:
     - S1 identity bar (logo, credential lines, member login)
     - S7 CTA band
     - S9 footer, both disclaimer paragraphs, footer links, copyright

   New to the subpages:
     - the sticky nav row linking the three pages plus a header CTA
   ============================================================================ */

/* ----------------------------------------------------------------------------
   ROUTES

   DEV NOTE — the three subpage paths below are placeholders. Final URLs are
   an open item (Google Ads subpages handoff, Section 5, item 3). Set them
   here once decided; nothing else in these files hardcodes a path.

   DEV NOTE — ACCESS_LANDING_ROUTE is where the logo, the header CTA and the
   footer CTA all send the visitor, per Arjun. Source attribution for
   subpage-to-landing-page traffic is believed to already be in place;
   confirm it carries, and correct this route if it changes.

   DEV NOTE — if a URL parameter is used to drop the visitor straight into
   the eligibility flow rather than the top of the landing page, append it to
   ACCESS_LANDING_ROUTE. The parameter name and value are dev's to define.
   ---------------------------------------------------------------------------- */

// export const ACCESS_LANDING_ROUTE = "/access/lbp";
export const ACCESS_LANDING_ROUTE = "/access/lbp";
export const ACCESS_ELIGIBILITY_ROUTE = "/access/lbp?start=1";

export const ACCESS_SUBPAGE_ROUTES = {
  about: "/access/lbp/about-the-access-program",
  how: "/access/lbp/how-it-works",
  ask: "/access/lbp/what-we-ask-for",
};

const NAV_ITEMS = [
  { key: "about", label: "About the ACCESS Program" },
  { key: "how", label: "How It Works" },
  { key: "ask", label: "What We Ask For" },
];

/* Scrolls a freshly mounted subpage to the top, matching the D2C subpages. */
export const useScrollToTop = () => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
};

export const AccessSubPageShell = ({ activePage, onMemberLogin, children }) => {
  const history = useHistory();

  /* Carries campaign/attribution params (gclid, variant, etc.) already on
     this subpage's URL forward onto internal navigation, including the hop
     to the landing page, so attribution isn't dropped moving between pages.
     Params already on the destination path (e.g. ACCESS_LANDING_ROUTE's own
     ?start=1) always win over an incoming same-named param. */
  const withSourceParams = (path) => {
    const incoming = window.location.search;
    if (!incoming) return path;
    const [base, existingQuery = ""] = path.split("?");
    const merged = new URLSearchParams(existingQuery);
    new URLSearchParams(incoming).forEach((value, key) => {
      if (!merged.has(key)) merged.set(key, value);
    });
    const qs = merged.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const go = (path) => (e) => {
    e.preventDefault();
    history.push(withSourceParams(path));
  };

  /* DEV NOTE — the landing page has a full handleMemberLogin routine that
     tears down Myra, shows the Flutter loader and calls
     window.navigateToMemberLogin. It is tightly coupled to that page, so it
     is not duplicated here. Pass it in as onMemberLogin if it gets extracted;
     until then the link routes to the ACCESS landing page, which has a
     working member login. */
  const handleMemberLogin = (e) => {
    e.preventDefault();
    // if (typeof onMemberLogin === "function") {
    //   onMemberLogin();
    //   return;
    // }
    handleLoginClick(history);
    history.push(ACCESS_LANDING_ROUTE);
  };

  return (
    <div className={styles.page}>
      {/* ===== S1 IDENTITY BAR ===== */}
      <div className={styles.idbar}>
        <div className={`${styles.container} ${styles.idbarIn}`}>
          <a className={styles.logoLink} href={withSourceParams(ACCESS_ELIGIBILITY_ROUTE)} onClick={go(ACCESS_ELIGIBILITY_ROUTE)} aria-label="Virdio Health — Medicare ACCESS">
            <span className={styles.logo}>
              <span className={styles.logoV}>virdio</span>
              <span className={styles.logoH}>health</span>
            </span>
          </a>
          <div className={styles.creds}>
            <strong>Medicare-enrolled provider</strong>
            <span>Participant in the CMS ACCESS Model</span>
          </div>
          <a className={styles.memberlogin} href="#" onClick={handleMemberLogin}>
            Member login
          </a>
        </div>
      </div>

      {/* ===== NEW: STICKY SUBPAGE NAV ===== */}
      <nav className={styles.navbar} aria-label="Medicare ACCESS pages">
        <div className={`${styles.container} ${styles.navbarIn}`}>
          <div className={styles.navLinks}>
            {NAV_ITEMS.map((item) => {
              const path = ACCESS_SUBPAGE_ROUTES[item.key];
              const isActive = item.key === activePage;
              return (
                <a key={item.key} className={`${styles.navLink} ${isActive ? styles.active : ""}`} href={path} onClick={go(path)} aria-current={isActive ? "page" : undefined}>
                  {item.label}
                </a>
              );
            })}
          </div>
          <a className={styles.navCta} href={withSourceParams(ACCESS_ELIGIBILITY_ROUTE)} onClick={go(ACCESS_ELIGIBILITY_ROUTE)}>
            Check if I qualify
          </a>
        </div>
      </nav>

      {children}

      {/* ===== S7 CTA ===== */}
      <section className={styles.ctaband}>
        <div className={styles.container}>
          <h2>Find out if you qualify.</h2>
          <p className={styles.narrow}>You will get an answer before you commit to anything. If you do not qualify, we will tell you plainly, and if there is another option we will point you to it.</p>
          <a className={`${styles.btn} ${styles.btnPrimary}`} href={withSourceParams(ACCESS_ELIGIBILITY_ROUTE)} onClick={go(ACCESS_ELIGIBILITY_ROUTE)}>
            Check if I qualify
          </a>
          <span className={styles.ctaMicro}>A few short questions.</span>
        </div>
      </section>

      {/* ===== S9 FOOTER ===== */}
      <footer className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.disclaimer}>
            <p>Virdio Health applied to the CMS ACCESS Model and was approved by the Centers for Medicare and Medicaid Services to participate as an ACCESS Participant. Approval to participate is not an endorsement of Virdio Health, or of any service it provides, by CMS or any other government agency.</p>
            <p>Myra guides your recovery experience using information you share with her. She is an AI recovery coach and is not a substitute for medical advice, diagnosis, or treatment from your doctor. Information gathered through the program may be used for care coordination and to report your progress to Medicare as required by the ACCESS Model.</p>
          </div>
          <div className={styles.footlinks}>
            <a rel="noopener noreferrer" target="_blank" href="/privacy">
              Privacy Policy
            </a>
            <a rel="noopener noreferrer" href="#">
              Notice of Privacy Practices
            </a>
            <a rel="noopener noreferrer" target="_blank" href="/terms">
              Terms of Use
            </a>
            <a rel="noopener noreferrer" href="/contact-us">
              Contact
            </a>
            <a href="https://www.virdiohealth.com" target="_blank" rel="noopener noreferrer">
              virdiohealth.com
            </a>
          </div>
          <div className={styles.copy}>© 2026 Virdio Health. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default AccessSubPageShell;
